import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Workspace API/MCP keys (issue #175) — a key pinned to a PERSON instead of
// one client. It reaches every client that person can already see in Pulse,
// re-resolved live on every call (no re-mint when their access changes), and
// an agent picks which one to query per call (clientId). Same dual-surface
// pattern as ApiKeysCard: 'admin' mints on behalf of a Pulse login (Admin →
// Users → edit user); 'my' mints for the LOGGED-IN user themselves (Settings
// → Integrations). The secret is shown ONCE at creation, same as a normal key.
export default function WorkspaceKeyCard({ scope = 'my', userId }) {
  const listFn = scope === 'admin' ? () => api.listUserWorkspaceApiKeys(userId) : () => api.listMyWorkspaceApiKeys();
  const createFn = scope === 'admin' ? (p) => api.createUserWorkspaceApiKey(userId, p) : (p) => api.createMyWorkspaceApiKey(p);
  const revokeFn = scope === 'admin' ? (id) => api.revokeUserWorkspaceApiKey(userId, id) : (id) => api.revokeMyWorkspaceApiKey(id);

  const [keys, setKeys] = useState(null);
  const [clients, setClients] = useState([]); // this person's live reachable menu
  const [denied, setDenied] = useState(false);
  const [name, setName] = useState('');
  const [withRows, setWithRows] = useState(false);
  const [withDrafts, setWithDrafts] = useState(false);
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState(null); // { name, secret } — shown once
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setFresh(null); setDenied(false);
    listFn().then((r) => { setKeys(r.keys || []); setClients(r.clients || []); }).catch(() => { setKeys([]); setDenied(true); });
  }, [scope, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!keys) return null;
  if (denied) return null; // flag off, or nothing to manage integrations for — keep the section quiet
  // Nothing to span yet (a brand-new admin login, a client with one client) —
  // the per-client ApiKeysCard already covers the single-client case.
  if (scope === 'my' && clients.length < 2 && keys.length === 0) return null;

  const create = async () => {
    setCreating(true);
    try {
      const scopes = ['read', ...(withRows ? ['read_rows'] : []), ...(withDrafts ? ['write'] : [])];
      const r = await createFn({ name: name.trim() || 'Untitled key', scopes });
      setFresh({ name: r.key.name, secret: r.secret });
      setName(''); setWithRows(false); setWithDrafts(false);
      setKeys((await listFn()).keys || []);
    } catch (e) { alert('Couldn’t create the key: ' + e.message); }
    setCreating(false);
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using it stops working immediately.`)) return;
    try { await revokeFn(k.id); setKeys((await listFn()).keys || []); }
    catch (e) { alert('Couldn’t revoke the key: ' + e.message); }
  };
  const copy = () => {
    (navigator.clipboard?.writeText(fresh.secret) || Promise.reject()).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); })
      .catch(() => window.prompt('Copy this key:', fresh.secret));
  };

  const active = keys.filter((k) => !k.revokedAt);
  const base = window.location.origin;
  return (
    <div style={card}>
      <div style={title}>Workspace key {scope === 'admin' ? '(for this login)' : '(all my clients)'}</div>
      <p style={sub}>
        {scope === 'admin'
          ? 'A key scoped to THIS login’s own Pulse permissions, not one client — it reaches every client this person can already see (re-resolved live; changing their client access updates it automatically, no re-mint).'
          : 'One key for every client you already work on — no reconnecting per client. It re-checks your Pulse access on every call, and an assistant picks which client to query with clientId.'}
        {' '}Full guide: <a href="/api-guide" target="_blank" rel="noreferrer" style={{ color: 'var(--brand)' }}>{base}/api-guide</a>
      </p>

      {clients.length > 0 && (
        <p style={sub}>
          Currently reaches: <b>{clients.map((c) => c.name).join(', ')}</b>
        </p>
      )}
      {clients.length === 0 && <p style={{ ...sub, fontWeight: 600 }}>Nothing reachable yet — check API access is on for at least one client.</p>}

      {fresh && (
        <div style={freshBox}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>“{fresh.name}” — copy this key now. It won’t be shown again.</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ ...pill, wordBreak: 'break-all' }}>{fresh.secret}</code>
            <button style={btn} onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
            <button style={ghostBtn} onClick={() => setFresh(null)}>Done</button>
          </div>
        </div>
      )}

      {active.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {active.map((k) => (
            <div key={k.id} style={row}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                  <code>{k.hint}</code> · {k.scopes.join(' + ')} · {k.lastUsedAt ? `last used ${new Date(k.lastUsedAt).toLocaleDateString()}` : 'never used'}
                </div>
              </div>
              <button style={dangerBtn} onClick={() => revoke(k)}>Revoke</button>
            </div>
          ))}
        </div>
      )}
      {active.length === 0 && !fresh && <p style={{ ...sub, marginBottom: 12 }}>No active workspace keys yet.</p>}

      {clients.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input style={input} placeholder="Key name (e.g. Claude workspace)" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            <button style={btn} onClick={create} disabled={creating}>{creating ? 'Creating…' : '+ New workspace key'}</button>
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={withRows} onChange={(e) => setWithRows(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16 }} />
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
              Also allow <b>row-level data</b> — the table behind a tile (e.g. customer &amp; ticketing records), on every reachable client.
              May include personal data; only grant this to tools that genuinely need it.
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={withDrafts} onChange={(e) => setWithDrafts(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16 }} />
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
              Also allow <b>creating drafts</b> — build audience segments and draft campaigns on the client it targets.
              Drafts always await human review &amp; approval in Pulse; connected tools can never send.
            </span>
          </label>
        </>
      )}
    </div>
  );
}

const card = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 16, marginTop: 16 };
const title = { fontSize: 14, fontWeight: 700, marginBottom: 6 };
const sub = { fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' };
const pill = { fontSize: 13, fontWeight: 600, background: 'var(--elevated, #f3f3f5)', border: '1px solid var(--hairline)', borderRadius: 8, padding: '8px 12px', userSelect: 'all' };
const freshBox = { border: '1px solid var(--brand)', borderRadius: 10, padding: 12, marginBottom: 12 };
const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px' };
const input = { flex: '1 1 200px', minWidth: 0, border: '1px solid var(--hairline)', borderRadius: 8, padding: '9px 12px', fontSize: 13.5, background: 'var(--card)', color: 'inherit' };
const btn = { border: '1px solid var(--hairline)', background: 'var(--brand)', color: '#fff', borderRadius: 980, padding: '9px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', minHeight: 40 };
const ghostBtn = { ...btn, background: 'transparent', color: 'var(--muted)' };
const dangerBtn = { ...ghostBtn, color: 'var(--danger, #dc2626)', flexShrink: 0 };
